import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IdentityConsentCard } from "@/components/agent/IdentityConsentCard";
import { FastLaneCard } from "@/components/agent/FastLaneCard";
import { AccessCard } from "@/components/agent/AccessCard";
import { BookingCard } from "@/components/agent/BookingCard";
import { DataDropCard } from "@/components/agent/DataDropCard";
import { USFlag } from "@/components/USFlag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("vale_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("vale_session", id);
  }
  return id;
};

const getVisitorId = () => {
  const allowed = localStorage.getItem("dl_cookies") !== "essential";
  const store = allowed ? localStorage : sessionStorage;
  let id = store.getItem("dane_visitor_id");
  if (!id) {
    id = crypto.randomUUID();
    store.setItem("dane_visitor_id", id);
  }
  if (!allowed) localStorage.removeItem("dane_visitor_id");
  return id;
};

const stripTags = (t) =>
  t.replace(/\s*\[\[CHIPS:[^\]]*\]\]\s*/gs, " ").replace(/\s*\[\[[^\]]*\]\]\s*/g, " ").trim();

const stripStreaming = (t) => stripTags(t).replace(/\[\[[^\]]*$/s, "").trimEnd();

const renderRich = (text) =>
  text.split("**").map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="font-bold text-white">{part}</strong> : part
  );

const DESK_COLORS = {
  "FRONT DESK": "#FF5C5C",
  SALES: "#FF5C5C",
  "CLIENT SERVICES": "#F59E0B",
  "BIZ DEV": "#FFFFFF",
  "PROJECT DESK": "#4ADE80",
};

const DESK_LABELS = {
  SALES: "the sales desk",
  "CLIENT SERVICES": "client services",
  "BIZ DEV": "partnerships",
  "PROJECT DESK": "the project desk",
};

const QUICK_STARTS = [
  { id: "work", label: "Work with Dane", sub: "Consumer operations, infrastructure, and the work in between.", send: "I'd like to work with Dane on a project" },
  { id: "data", label: "Data and audience", sub: "Cleaning files, filling in what's missing, and finding more of the right people.", send: "I need help with a data file — cleaning it up, filling in gaps, or finding new prospects" },
  { id: "client", label: "Already a client?", sub: "Enter your access code and pick up where you left off.", action: "access" },
  { id: "status", label: "Check on a project", sub: "Get a status update on work that's underway.", send: "I'm already working with Dane — I'd like a status update" },
  { id: "partner", label: "Partnerships and press", sub: "Tell me what you have in mind.", send: "I want to talk about a partnership" },
];

export default function AgentConsole() {
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [started, setStarted] = useState(false);
  const [verified, setVerified] = useState(false);
  const [inside, setInside] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [user, setUser] = useState(null);
  const [desk, setDesk] = useState("FRONT DESK");
  const deskRef = useRef("FRONT DESK");
  const sessionId = useRef(getSessionId());
  const visitorId = useRef(getVisitorId());
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const pendingBookingRef = useRef(false);
  const identityPendingRef = useRef(false);
  const fileInputRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const nudgedRef = useRef(false);
  const messagesRef = useRef([]);
  const streamingRef = useRef(false);
  messagesRef.current = messages;
  streamingRef.current = streaming;

  useEffect(() => {
    document.title = "Talk to Dane — Dane Lomax";
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);

  useEffect(() => {
    const t = setInterval(async () => {
      if (nudgedRef.current || streamingRef.current) return;
      const userMsgs = messagesRef.current.filter((m) => m.role === "user").length;
      if (userMsgs < 1 || Date.now() - lastActivityRef.current < 60000) return;
      nudgedRef.current = true;
      try {
        const res = await fetch(`${API}/nudge`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId.current }),
        });
        const data = await res.json();
        if (data.ok && data.text) setMessages((m) => [...m, { role: "agent", content: data.text }]);
      } catch {}
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const routeDesk = useCallback((next) => {
    if (!next || next === deskRef.current) return;
    deskRef.current = next;
    setDesk(next);
  }, []);

  const processFile = async (f) => {
    if (!f) return;
    setStarted(true);
    setUploading(true);
    lastActivityRef.current = Date.now();
    setMessages((m) => [...m, { role: "user", content: `Here's a sample file: ${f.name}` }]);
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId.current);
      fd.append("file", f);
      const res = await fetch(`${API}/data/analyze`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Analysis failed");
      setMessages((m) => [...m, { role: "card", card: "datadrop", data }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "agent", content: err.message || "I couldn't read that file — CSV only, 10MB max." }]);
    }
    setUploading(false);
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    processFile(f);
  };

  const handleVerified = useCallback(() => {
    setVerified(true);
    identityPendingRef.current = false;
    if (pendingBookingRef.current) {
      pendingBookingRef.current = false;
      setMessages((m) => [...m, { role: "card", card: "booking" }]);
    }
  }, []);

  const handleInside = useCallback((data) => {
    setInside(data);
    setVerified(true);
    identityPendingRef.current = false;
    routeDesk(data?.tier === "owner" ? "PROJECT DESK" : "CLIENT SERVICES");
    if (pendingBookingRef.current) {
      pendingBookingRef.current = false;
      setMessages((m) => [...m, { role: "card", card: "booking" }]);
    }
  }, [routeDesk]);

  useEffect(() => {
    (async () => {
      try {
        if (localStorage.getItem("dane_signed_in") === "1") {
          const r = await fetch(`${API}/auth/me`, { credentials: "include" });
          if (r.ok) {
            const me = await r.json();
            setUser(me);
            if (me.visitor_id && me.visitor_id !== visitorId.current) {
              localStorage.setItem("dane_visitor_id", me.visitor_id);
              visitorId.current = me.visitor_id;
            }
          } else {
            localStorage.removeItem("dane_signed_in");
          }
        }
      } catch {}
      fetch(`${API}/config?session_id=${sessionId.current}&visitor_id=${visitorId.current}`).then((r) => r.json()).then(setConfig).catch(() => {});
      fetch(`${API}/sessions/${sessionId.current}/contact`).then((r) => r.json()).then((d) => {
        setVerified(d.verified);
        if (d.inside) {
          setInside({ ...d.inside, broadcast: d.broadcast?.message });
          routeDesk(d.inside.tier === "owner" ? "PROJECT DESK" : "CLIENT SERVICES");
        }
      }).catch(() => {});
    })();
  }, [routeDesk]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamText]);

  const send = useCallback(async (text) => {
    if (!text.trim() || streaming) return;
    setStarted(true);
    setInput("");
    lastActivityRef.current = Date.now();
    setMessages((m) => [...m, { role: "user", content: text }]);
    setStreaming(true);
    setStreamText("");
    let acc = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, visitor_id: visitorId.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let actions = [];
      let chips = [];
      let nextDesk = null;
      let pmLog = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "delta") {
            acc += ev.content;
            setStreamText(stripStreaming(acc));
          } else if (ev.type === "done") {
            actions = ev.actions || [];
            chips = ev.chips || [];
            nextDesk = ev.desk || null;
            pmLog = ev.pm_log || [];
          }
        }
      }
      const hasCard = actions.some((a) => ["verify_identity", "request_access", "book_meeting"].includes(a));
      const final = [];
      if (nextDesk && nextDesk !== deskRef.current) {
        final.push({ role: "system", content: `You're with ${DESK_LABELS[nextDesk] || nextDesk.toLowerCase()} now`, desk: nextDesk });
        routeDesk(nextDesk);
      }
      final.push({ role: "agent", content: stripTags(acc), chips: hasCard ? [] : chips, desk: nextDesk || deskRef.current });
      pmLog.forEach((l) => final.push({ role: "system", content: `Board update — ${l.charAt(0)}${l.slice(1).toLowerCase()}`, desk: "PROJECT DESK" }));
      if (actions.includes("pending_booking")) pendingBookingRef.current = true;
      if (actions.includes("verify_identity") && !identityPendingRef.current) {
        identityPendingRef.current = true;
        final.push({ role: "card", card: "identity" });
      }
      if (actions.includes("request_access")) final.push({ role: "card", card: "access" });
      if (actions.includes("book_meeting")) final.push({ role: "card", card: "booking" });
      if (actions.includes("delete_data")) {
        try {
          const dr = await fetch(`${API}/data-deletion`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId.current, visitor_id: visitorId.current }),
          });
          const dd = await dr.json();
          localStorage.removeItem("dane_visitor_id");
          sessionStorage.removeItem("dane_visitor_id");
          localStorage.removeItem("vale_session");
          final.push({ role: "system", content: dd.message || "Your data has been deleted." });
        } catch {
          final.push({ role: "system", content: "The deletion didn't complete — please try again." });
        }
      }
      setMessages((m) => [...m, ...final]);
    } catch {
      setMessages((m) => [...m, { role: "agent", content: "The connection dropped — please try that again." }]);
    }
    setStreamText("");
    setStreaming(false);
    lastActivityRef.current = Date.now();
  }, [streaming, routeDesk]);

  const lastAgentIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "agent") return i;
      if (messages[i].role === "user") return -1;
    }
    return -1;
  })();

  const signOut = () => {
    localStorage.removeItem("dane_signed_in");
    fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).finally(() => window.location.reload());
  };

  const newConversation = () => {
    localStorage.removeItem("vale_session");
    window.location.reload();
  };

  const forgetMe = () => {
    localStorage.removeItem("dane_signed_in");
    fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).finally(() => {
      localStorage.removeItem("dane_visitor_id");
      localStorage.removeItem("vale_session");
      window.location.reload();
    });
  };

  const inputForm = (
    <form
      onSubmit={(e) => { e.preventDefault(); send(input); }}
      className="flex items-center gap-2 rounded-2xl border border-[#2E2E2E] bg-[#111111] p-2 pl-5 focus-within:border-[#FF5C5C]"
    >
      <input
        ref={inputRef}
        value={input}
        onChange={(e) => { setInput(e.target.value); lastActivityRef.current = Date.now(); }}
        placeholder={uploading ? "Reading your file..." : "What can I help you with?"}
        data-testid="chat-input"
        className="flex-1 bg-transparent text-lg font-medium py-3 outline-none text-white placeholder:text-[#D6D6D6] caret-[#FF5C5C] min-w-0"
      />
      <input type="file" accept=".csv" ref={fileInputRef} onChange={handleFile} className="hidden" data-testid="file-input" />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        data-testid="file-drop-button"
        aria-label="Attach a sample CSV"
        className="p-3 rounded-xl text-base font-semibold text-[#D6D6D6] hover:text-white hover:bg-[#2E2E2E] disabled:opacity-40 transition-colors duration-150 whitespace-nowrap"
      >
        Attach CSV
      </button>
      <motion.button
        type="submit"
        disabled={streaming || !input.trim()}
        whileTap={{ scale: 0.95 }}
        data-testid="chat-send-button"
        aria-label="Send message"
        className="rounded-xl bg-white text-black font-semibold text-lg px-5 min-h-[44px] hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150"
      >
        Send
      </motion.button>
    </form>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="agent-console">
      <header className="fixed top-0 left-0 w-full h-16 z-50 flex items-center justify-between px-5 md:px-10 border-b border-[#2E2E2E] bg-[#050505]">
        <a href="/" className="font-display text-xl text-white hover:text-[#D6D6D6] transition-colors duration-150" data-testid="agent-name">
          Dane Lomax
        </a>
        <div className="flex items-center gap-3 md:gap-4">
          {started && (
            <button onClick={newConversation} data-testid="new-conversation-button"
              className="hidden sm:block text-base font-semibold text-[#D6D6D6] hover:text-white transition-colors duration-150">
              New conversation
            </button>
          )}
          {user ? (
            <>
              <span className="flex items-center gap-2" data-testid="user-chip" title={user.email}>
                {user.picture && <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-7 h-7 rounded-full border border-[#2E2E2E]" />}
                <span className="hidden sm:inline text-base font-semibold text-white">{(user.name || user.email).split(" ")[0]}</span>
              </span>
              <button onClick={signOut} data-testid="signout-button"
                className="text-base font-semibold text-[#D6D6D6] hover:text-white transition-colors duration-150">
                Sign out
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="google-signin-button"
              className="text-base font-semibold border border-[#2E2E2E] rounded-full px-4 min-h-[44px] text-white hover:bg-white hover:text-black transition-colors duration-150"
            >
              Sign in
            </button>
          )}
        </div>
      </header>

      <main className={`relative z-10 w-full max-w-3xl mx-auto px-5 md:px-8 pt-24 ${started ? "pb-52" : "pb-24"}`}>
        {inside && (
          <div className="sticky top-16 z-40 -mx-5 md:-mx-8 px-5 md:px-8 border-b border-[#2E2E2E] bg-[#111111]" data-testid="inside-panel">
            <div className="py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-base font-bold text-[#FF5C5C]">Client access</span>
              <span className="text-base font-semibold text-white">{inside.name}</span>
              {(inside.project || inside.company) && <span className="text-base font-medium text-[#D6D6D6]">{inside.project || inside.company}</span>}
              {inside.broadcast && (
                <span className="text-base font-medium text-[#F59E0B] w-full sm:w-auto" data-testid="insider-broadcast">
                  Note from Dane: {inside.broadcast}
                </span>
              )}
              <button onClick={() => setMessages((m) => [...m, { role: "card", card: "booking" }])} data-testid="priority-call-button"
                className="ml-auto text-base font-semibold rounded-full border border-[#F59E0B] px-4 min-h-[44px] text-white hover:bg-[#F59E0B] hover:text-black transition-colors duration-150">
                Book a call
              </button>
            </div>
          </div>
        )}

        {!started && config && (
          <div className="pt-4 md:pt-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
              data-testid="greeting-block"
            >
              {config.known_visitor && (
                <p className="text-base font-semibold mb-3 text-[#D6D6D6]" data-testid="recognized-line">
                  Welcome back{config.known_visitor.name ? `, ${config.known_visitor.name.split(" ")[0]}` : ""}.
                </p>
              )}
              <h1 className="font-display text-[30px] md:text-[38px] leading-[1.1] text-white" data-testid="talk-headline">
                Tell me what you're working with.
              </h1>
            </motion.div>

            <div className="mt-8">
              <p className="text-[19px] font-medium text-[#D6D6D6] mb-3" data-testid="agent-greeting">{config.greeting}</p>
              {inputForm}
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="process-options">
              {QUICK_STARTS.map((b, i) => (
                <motion.button
                  key={b.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ delay: i * 0.06, duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
                  onClick={() => {
                    if (b.action === "access") {
                      setStarted(true);
                      routeDesk("CLIENT SERVICES");
                      setMessages((m) => [...m, { role: "card", card: "access" }]);
                    } else {
                      send(b.send);
                    }
                  }}
                  data-testid={`prompt-chip-${i}`}
                  className="group relative flex flex-col text-left rounded-2xl border border-[#2E2E2E] bg-[#111111] p-5 hover:border-[#FF5C5C] transition-colors duration-200"
                >
                  <span className="text-lg font-bold text-white leading-snug">{b.label}</span>
                  <span className="mt-1 text-base font-medium text-[#D6D6D6] leading-relaxed">{b.sub}</span>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-10 mt-8">
          <AnimatePresence>
            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}>
                {m.role === "card" ? (
                  m.card === "identity" ? (
                    user ? (
                      <FastLaneCard sessionId={sessionId.current} visitorId={visitorId.current} api={API} user={user} consentText={config?.consent_text} onVerified={handleVerified} />
                    ) : (
                      <IdentityConsentCard sessionId={sessionId.current} visitorId={visitorId.current} api={API} consentText={config?.consent_text} onVerified={handleVerified} defaultName={user?.name} defaultEmail={user?.email} />
                    )
                  ) : m.card === "access" ? (
                    <AccessCard sessionId={sessionId.current} visitorId={visitorId.current} api={API} onInside={handleInside} />
                  ) : m.card === "datadrop" ? (
                    <DataDropCard data={m.data} />
                  ) : (
                    <BookingCard sessionId={sessionId.current} api={API} defaultName={user?.name} defaultEmail={user?.email} />
                  )
                ) : m.role === "system" ? (
                  <div className="flex items-center gap-4" data-testid="desk-reroute-line">
                    <span className="h-px flex-1 bg-[#2E2E2E]" />
                    <span className="text-base font-semibold" style={{ color: DESK_COLORS[m.desk] || "#FF5C5C" }}>
                      {m.content}
                    </span>
                    <span className="h-px flex-1 bg-[#2E2E2E]" />
                  </div>
                ) : m.role === "user" ? (
                  <div className="flex flex-col items-end" data-testid="message-user">
                    <p className="text-base font-semibold text-[#D6D6D6] mb-1.5">You</p>
                    <p className="max-w-[85%] rounded-2xl rounded-tr-sm bg-[#111111] border border-[#2E2E2E] px-5 py-3 text-[17px] font-medium text-white leading-[1.6] whitespace-pre-wrap">
                      {m.content}
                    </p>
                  </div>
                ) : (
                  <div data-testid="message-agent">
                    <p className="text-base font-bold mb-2" style={{ color: DESK_COLORS[m.desk] || "#FF5C5C" }}>
                      Dane{m.desk && m.desk !== "FRONT DESK" ? ` — ${DESK_LABELS[m.desk] || m.desk.toLowerCase()}` : ""}
                    </p>
                    <p className="text-[19px] font-medium text-white leading-[1.6] whitespace-pre-wrap">
                      {renderRich(m.content)}
                    </p>
                    {i === lastAgentIdx && !streaming && (m.chips || []).length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-4" data-testid="follow-up-chips">
                        {m.chips.map((c, ci) => (
                          <motion.button
                            key={c}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ delay: 0.3 + ci * 0.08, duration: 0.3 }}
                            onClick={() => send(c)}
                            data-testid={`follow-up-chip-${ci}`}
                            className="rounded-full bg-[#111111] border border-[#2E2E2E] px-5 min-h-[44px] text-base font-semibold text-[#D6D6D6] hover:bg-white hover:text-black transition-colors duration-200"
                          >
                            {c}
                          </motion.button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {streaming && (
            <div data-testid="streaming-message">
              <p className="text-base font-bold mb-2" style={{ color: DESK_COLORS[desk] || "#FF5C5C" }}>
                Dane{desk !== "FRONT DESK" ? ` — ${DESK_LABELS[desk] || desk.toLowerCase()}` : ""}
              </p>
              <p className="text-[19px] font-medium text-white leading-[1.6] whitespace-pre-wrap">
                {renderRich(streamText || "…")}
              </p>
            </div>
          )}
        </div>
        <div ref={bottomRef} className="h-4" />
      </main>

      {started && (
        <div className="fixed bottom-14 left-0 w-full z-40 px-5 md:px-8">
          <div className="max-w-3xl mx-auto">{inputForm}</div>
        </div>
      )}

      <footer className="fixed bottom-0 left-0 w-full h-12 z-50 bg-[#050505] border-t border-[#2E2E2E] flex items-center justify-center gap-4 px-4 text-base font-semibold text-[#D6D6D6] whitespace-nowrap overflow-hidden">
        <span className="flex items-center gap-2 text-white" data-testid="made-in-usa">
          <USFlag width={22} height={13} /> <span className="hidden sm:inline">Proudly Made in the USA</span>
        </span>
        <a href="/privacy" className="hover:text-white transition-colors duration-150" data-testid="privacy-link">Privacy</a>
        <a href="/terms" className="hover:text-white transition-colors duration-150" data-testid="terms-link">Terms</a>
        <a href="/admin" className="hover:text-white transition-colors duration-150" data-testid="admin-link">Admin</a>
        {config?.known_visitor && (
          <button onClick={forgetMe} className="hover:text-white transition-colors duration-150" data-testid="forget-me-link">Forget me</button>
        )}
        {verified && <span className="text-[#4ADE80]" data-testid="verified-indicator">Verified</span>}
      </footer>
    </div>
  );
}
